import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, AlertTitle, Button, Paper, Typography } from '@mui/material';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import { useNavigate } from 'react-router-dom';
import {
  getIpamActiveTeardown, getIpamConfig, getIpamTeardownPreview, IPAM_NOT_CONFIGURED,
} from '../../../../services/ipamApi';
import IpamSetup, { SCOPE_STEP } from './IpamSetup';
import IpamDashboard from './IpamDashboard';
import IpamBlockedPanel, { parseIpamAccountMessage } from './IpamBlockedPanel';
import OperationProgress from '../OperationProgress';
import { POLL_INTERVAL_MS } from '../pollInterval';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionHeader,
} from '../../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import { CK, FONT_BODY, sectionCardSx } from '../../portalStyles';
// The teardown copy is IMPORTED, NEVER RETYPED. Every sentence below that states
// what a teardown does to a customer's network, or what it costs them, was once
// hand-copied into this file - two of them with a typographic apostrophe instead
// of the deliberate ASCII one. Nothing looked wrong, and that was the problem:
// the verification step greps these sentences verbatim, so it was finding one
// occurrence out of four while this screen went on saying its own version.
import {
  IPAM_BILLING_STOPS, IPAM_NO_VPC_DELETED,
  IPAM_TEARDOWN_BLOCKED, ipamResidueMeterVerdict,
} from './ipamCopy';

// Slate / blue-grey 700. VPC IPAM is NOT a registered engine, so there is no
// engines/ipam.js to hold this - the hex is duplicated as a local const per page
// file, which is the convention engines/accessanalyzer.js's colour comment sets.
// Slate was chosen because every saturated hue left collides with the severity
// palette or the utilization bars that share these screens.
const IPAM_COLOR = '#455A64';

// Consecutive background-poll failures tolerated before the error card replaces
// the progress narration. At the shared 3s cadence a hard fault surfaces after
// roughly nine seconds; one blipped poll never does.
const MAX_POLL_FAILURES = 3;

// THIS PAGE IS THE ONLY THING BETWEEN AN UN-ENABLED TENANT AND A BLANK SCREEN.
//
// IPAM is deliberately not registered as an Engine (D-2), so GET /capabilities
// carries no `ipam` row and the sidebar group renders whether or not the tenant
// has ever enabled it - exactly as AWS Config's does. That is accepted, and it is
// accepted BECAUSE this entry point resolves the un-enabled state into the setup
// wizard rather than into nothing. Every IPAM nav click lands here first.
const ipamHeader = (
  <PortalPageHeader
    icon={<LanOutlinedIcon sx={{ fontSize: 24 }} />}
    title="VPC IPAM"
    subtitle="Address space, pools and public IPs across your enrolled accounts"
    color={IPAM_COLOR}
  />
);

// notConfigured matches the service's own sentinel rather than a re-spelling of
// it. The service wraps the error (`...: %w`), so the test is `includes`, never
// equality, and it is case-folded because the wrap can capitalise the sentence.
//
// GET /ipam/config does not currently reject for this case at all - it answers
// 200 with data:null (DD-33) - so this arm is defensive. It exists because
// treating that state as a fault would paint a red error card over the one screen
// whose entire job is to offer the enable button, which is the single worst
// failure this page has: it renders "broken product" where "un-enabled product"
// is the truth.
const notConfigured = (e) =>
  String(e?.message || '').toLowerCase().includes(IPAM_NOT_CONFIGURED.toLowerCase());

// parseResidue reads the teardown job's BEST-EFFORT lane: a JSON map[accountID]string
// of things a teardown left behind without failing over them.
//
// It is a distinct column from errorsJson (the strict lane, the failures that stop
// a teardown) and the two must not be merged, because merging them makes "did this
// teardown succeed?" unanswerable. This banner is about the best-effort lane only:
// resources still standing in named accounts, on an IPAM that is still metering.
function parseResidue(job) {
  const raw = job?.residueJson;
  if (!raw) return [];
  let decoded = raw;
  if (typeof raw === 'string') {
    try {
      decoded = JSON.parse(raw);
    } catch (_) {
      return [];
    }
  }
  if (!decoded || typeof decoded !== 'object' || Array.isArray(decoded)) return [];
  return Object.entries(decoded).filter(([, v]) => typeof v === 'string' && v.trim() !== '');
}

// residueOrFallback is why this banner fires at all.
//
// GET /ipam/teardown/active answers only a NON-TERMINAL job — its predicate is
// `status NOT IN ipamTeardownTerminalStatuses` (ipam_teardown.go:143, 894-906).
// A teardown that ends in partial_failure settles its job as `failed`, which IS
// terminal. So for the exact case this banner exists for, the live-job read
// returns null and parseResidue returns [] — and a residue-only gate would be
// dead code that "fixes" the false alarm by deleting the real warning.
//
// config.errorMessage is the durable second copy: a half-failed teardown writes
// the keyed map there as `account: message | account: message`. It is parsed
// STRICTLY (parseIpamAccountMessage aborts the whole parse on one segment that is
// not `<12-digit account>: …`), which is what keeps the other two writers of
// partial_failure out — the enable fan-out's "%d of %d accounts failed to enrol"
// and the region sync's message carry no account key and can never match.
function residueOrFallback(job, errorMessage) {
  const live = parseResidue(job);
  if (live.length > 0) return live;
  const parsed = parseIpamAccountMessage(errorMessage);
  if (!parsed) return [];
  return Object.entries(parsed).filter(([, v]) => typeof v === 'string' && v.trim() !== '');
}

// transitionCopy picks the in-progress wording from the config's progressLabel.
//
// progressLabel is the ONLY reliable discriminator between the fan-outs: there is
// no per-operation status, every one of them writes `in_progress`, and the label
// is stamped in the same UPDATE as the status. errorMessage is useless here - the
// disable path never writes it and the member paths clear it.
//
// FIVE ARMS, AND THE ORDER MATTERS. `tier` is tested before `updat` because a
// future "Updating the IPAM tier" contains both and the tier arm is the one that
// must win: Advanced -> Free is a long precondition chain that can fail minutes in
// on "pools still exist", and captioning that as a settings save is how a failed,
// still-metering downgrade reads as a saved preference.
//
// Enable is the FALLBACK rather than a matched case because it is the one path
// that can briefly show an empty or stale label - activate flips the status
// before the fan-out goroutine gets around to stamping its own.
function transitionCopy(progressLabel) {
  const hint = (progressLabel || '').toLowerCase();
  if (hint.includes('estimat')) {
    return {
      title: 'Preparing your cost estimate…',
      body: 'Attaching the scoped read permissions Prism needs in each account and counting the IP '
        + 'addresses attached to their network interfaces. No AWS resource has been created and '
        + 'nothing is billing yet - the Advanced tier only starts when you accept the cost on the '
        + 'next screen.',
    };
  }
  if (hint.includes('disabl')) {
    return {
      title: 'Disabling VPC IPAM…',
      body: 'Disassociating each account’s resource discovery, deleting its RAM share, deleting the '
        + 'discovery itself, releasing the allocations Prism made, deleting the pools and scopes '
        + 'Prism created, and finally deleting the IPAM - in that order. '
        // Interpolated whole from ipamCopy.js rather than spelled here: it is the
        // sentence that answers the question an operator actually has while a
        // teardown runs, and a copy audit has to be able to find it - verbatim,
        // ASCII apostrophe and all - on every screen that states it.
        + `${IPAM_NO_VPC_DELETED} `
        + 'An IPAM, scope or pool that already existed and that Prism adopted is left running, '
        + `because its address history is yours. ${IPAM_BILLING_STOPS}`,
    };
  }
  if (hint.includes('tier')) {
    return {
      title: 'Changing the IPAM tier…',
      body: 'Moving to the Free tier is not a simple setting change: AWS refuses it until every '
        + 'private-scope pool, every non-default private scope, every pool outside the home Region’s '
        + 'locale, every non-default resource-discovery association and every allocation not owned by '
        + 'the IPAM owner has been removed. This can take several minutes and can fail late, on a '
        + 'precondition rather than on the tier itself.',
    };
  }
  if (hint.includes('updat')) {
    return {
      title: 'Updating VPC IPAM…',
      body: 'Applying the requested operating Regions to the IPAM and across every account’s resource '
        + 'discovery. Discovery only finds address space in the Regions on this list, so a Region '
        + 'removed here stops being inventoried.',
    };
  }
  return {
    title: 'Enabling VPC IPAM across accounts…',
    body: 'Creating the IPAM in the host account, creating a resource discovery in each enrolled '
      + 'account, sharing it back to the host through AWS RAM and associating it. Accounts are '
      + 'enrolled independently, so one failure never stops the rest. After that, AWS’s first '
      + 'discovery sweep takes up to about 20 minutes before any address space appears - that wait '
      + 'is normal and there is nothing to retry.',
  };
}

// IpamEntry decides what to render at /securityhub/ipam. Seven states, in this
// order, and the order is the design:
//
//   1. loading                     -> PortalLoading
//   2. error                       -> PortalErrorState
//   3. in_progress                 -> OperationProgress + transitionCopy
//   4. awaiting_consent            -> IpamSetup, resumed at the cost gate
//   5. blocked, OR a teardown job
//      parked at `blocked`         -> IpamBlockedPanel
//   6. no config / disabled/failed -> IpamSetup, from the start
//   7. enabled / partial_failure   -> IpamDashboard
//
// STATE 5 HAS TWO ROUTES IN, and the second one is a JOB status rather than a
// config status. See the branch itself for why.
//
// The error card is deliberately checked BEFORE the in_progress branch, which is
// exactly why the background-poll path has to fail MAX_POLL_FAILURES times first:
// reporting every rejection would let one blipped poll replace a multi-minute
// enable or teardown narration with a red failure, during the very operation this
// screen exists to narrate. Staying silent forever is the opposite lie, so a
// sustained outage does surface, and the next good tick clears it.
//
// partial_failure routes to the DASHBOARD, never back to the wizard. Accounts
// enrol independently and one account's failure never blocks the others, so
// partial_failure means "most of this is live and discovering". Re-running the
// wizard would re-begin the config, which resets consent and tears the working
// accounts back down; repair is per-account, on the Accounts page. The one
// partial_failure that does NOT reach the dashboard is the one whose teardown job
// is still parked at `blocked` — that is a stopped teardown wearing a
// re-beginnable config status, and it gets state 5.
export default function IpamEntry() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [residue, setResidue] = useState([]);
  // THE PRE-FLIGHT'S METER VERDICT, HELD AS A THIRD VALUE. `undefined` means the
  // ledger has not been read; it is never collapsed into `false`. See
  // ipamResidueMeterVerdict for why nothing else on this page may decide it.
  //
  // DIRECTION AND CONFIDENCE ARE ONE STATE, NOT TWO. `billingVerified` only means
  // anything about the `stillBilling` it arrived with, so holding them in separate
  // useStates would let one render land with the new direction and the old
  // confidence — a stale `verified: true` vouching for a verdict it never saw is
  // exactly the hidden-charge direction this pair exists to prevent. One object,
  // one setter, one payload.
  const [previewMeter, setPreviewMeter] = useState({
    stillBilling: undefined,
    verified: undefined,
    note: '',
  });
  // THE ACTIVE-TEARDOWN READ IS NOW A ROUTING INPUT, NOT ONLY A BANNER INPUT, so
  // it carries the third answer as well as the two. `jobStatus` is the job's own
  // status when the read landed and null when it did not; `probed` says which of
  // those two a null is. Collapsing them would make "no blocked teardown" and
  // "Prism has not looked yet" the same value, and the page would answer with the
  // dashboard while the question was still outstanding.
  const [teardownJob, setTeardownJob] = useState({ probed: false, jobStatus: null });
  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const refresh = useCallback(async ({ showSpinner = true } = {}) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getIpamConfig();
      // `data: null` IS THE WIZARD SIGNAL, not a failure. A tenant that has never
      // enabled VPC IPAM gets HTTP 200 with a null body by design (DD-33), so the
      // un-enabled state arrives here as a RESOLVED NULL. Reading it as an error -
      // or, worse, rendering nothing - is what turns an un-enabled capability into
      // an apparently broken one.
      setConfig(res?.data || null);
      pollFailuresRef.current = 0;
      setError(null);
    } catch (e) {
      if (notConfigured(e)) {
        setConfig(null);
        pollFailuresRef.current = 0;
        setError(null);
      } else if (showSpinner) {
        // A user-visible load has nothing to fall back on and reports at once.
        setError(e.message);
      } else {
        pollFailuresRef.current += 1;
        if (pollFailuresRef.current >= MAX_POLL_FAILURES) setError(e.message);
      }
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while a transition runs so the page advances by itself when the
  // orchestrator goroutine finishes.
  //
  // THE DEPENDENCY IS `config?.status`, A PRIMITIVE - NEVER `config`. Depending on
  // the whole object rebuilds the interval on every progress tick, so the real
  // cadence drifts to POLL_INTERVAL_MS + RTT and, worse, the failure counter is
  // reset by the teardown on every cycle, which means MAX_POLL_FAILURES can never
  // be reached and a dead backend narrates as a bar that is still "working".
  useEffect(() => {
    if (config?.status !== 'in_progress') return undefined;
    // Each transition starts clean: failures tolerated during an earlier operation
    // must not count against this one.
    pollFailuresRef.current = 0;
    pollRef.current = setInterval(() => refresh({ showSpinner: false }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [config?.status, refresh]);

  // The teardown-residue read, on a boolean derived from the status - a primitive,
  // for the same reason the poll above is: keying it on `config` would refire it on
  // every progress tick of an unrelated operation.
  //
  // GET /ipam/teardown/active is one of the routes still answering 501, which
  // arrives here as a rejection. It is deliberately not special-cased and not
  // mocked: the banner stays absent, which is the correct rendering for "Prism
  // cannot see a teardown", and it lights up with no edit here the day the service
  // method lands.
  // Scoped to partial_failure alone, which is the status a half-failed teardown
  // writes (ipam_teardown.go:1527/1545/1628). An `enabled` config has no teardown
  // to have left residue behind, so widening this would spend a request on every
  // warm dashboard load to learn nothing.
  const teardownRelevant = config?.status === 'partial_failure';
  useEffect(() => {
    if (!teardownRelevant) {
      setResidue([]);
      setTeardownJob({ probed: false, jobStatus: null });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getIpamActiveTeardown();
        if (cancelled) return;
        setResidue(residueOrFallback(res?.data, config?.errorMessage));
        setTeardownJob({ probed: true, jobStatus: res?.data?.status || null });
      } catch (_) {
        // The live job could not be read at all. Fall back to the config's own
        // copy rather than to silence, for the same reason the null case does.
        // `probed` still flips: an unreadable job is an ANSWERED question for
        // routing purposes — Prism looked, learned nothing, and must stop holding
        // the screen back rather than spin forever on a route that answers 501.
        if (cancelled) return;
        setResidue(residueOrFallback(null, config?.errorMessage));
        setTeardownJob({ probed: true, jobStatus: null });
      }
    })();
    return () => { cancelled = true; };
    // errorMessage is a STRING, so this stays a primitive dep array — the reason
    // this effect keys on `teardownRelevant` rather than on `config` is to avoid
    // refiring on every progress tick of an unrelated operation, and a string
    // that only changes when the failure changes does not reintroduce that.
  }, [teardownRelevant, config?.errorMessage]);

  // ── THE METER READ HAS ITS OWN GATE, AND IT IS WIDER THAN THE ROUTING ONE ──
  //
  // `blocked` is the OTHER durable state where an IPAM sits there metering, and it
  // is the one screen in this capability that never read the pre-flight verdict at
  // all: IpamBlockedPanel states what a teardown DOES and says nothing about what
  // the meter IS. That gap is about to widen — a config parked at `blocked` yields
  // no live ledger observation, so the verdict behind it is unverified for every
  // tenant, and the panel would be the last surface still printing flat sentences
  // over payloads Prism cannot stand behind.
  //
  // THIS IS A SEPARATE EFFECT ON PURPOSE, rather than a widened `teardownRelevant`.
  // That flag is a ROUTING input — `teardownJobBlocked` and `teardownUnresolved`
  // are both derived from it, and `teardownUnresolved` decides whether this page
  // HOLDS THE SCREEN instead of rendering. Widening it to buy a data fetch would
  // put two route predicates on a status they were deliberately scoped away from,
  // for a reason that has nothing to do with routing. The fetch needs a wider gate;
  // the router does not, and does not get one.
  const meterRelevant = config?.status === 'partial_failure' || config?.status === 'blocked';
  useEffect(() => {
    if (!meterRelevant) {
      // All three back to "not read", TOGETHER. `verified` and `note` only mean
      // anything about the `stillBilling` they arrived with; clearing one and
      // leaving another is how a confidence from a previous payload ends up
      // vouching for a verdict it never saw.
      setPreviewMeter({ stillBilling: undefined, verified: undefined, note: '' });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getIpamTeardownPreview();
        if (!cancelled) {
          setPreviewMeter({
            stillBilling: res?.data?.stillBilling,
            verified: res?.data?.billingVerified,
            note: String(res?.data?.billingNote || '').trim(),
          });
        }
      } catch (_) {
        // A FAILED READ IS THE HEDGE, NEVER A "NO". Undefined on every axis routes
        // the copy to its "Prism has not read the ledger, treat it as billing" arm.
        if (!cancelled) setPreviewMeter({ stillBilling: undefined, verified: undefined, note: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [meterRelevant]);

  // THE BACKEND'S OWN PROSE ABOUT THIS TEARDOWN, when there is any: a message
  // that does NOT parse as the per-account failure map is free prose the settle
  // arm wrote, and it is rendered verbatim rather than summarised.
  const teardownProse = useMemo(() => {
    const text = String(config?.errorMessage || '').trim();
    if (!text) return '';
    return parseIpamAccountMessage(text) ? '' : text;
  }, [config?.errorMessage]);

  // MAY THIS BANNER SAY THE METER IS RUNNING?
  //
  // `const teardownMeterAsserted = !teardownProse;` INFERRED A LIVE METER FROM A
  // STRING'S SHAPE: a message that parsed as the per-account map was read as
  // proof the IPAM was still up. It is not. The settle arm that writes a
  // per-account map CO-OCCURS with a successful DeleteIpam — a host-side step can
  // fail into run.errs without aborting the stage loop, and stage 9/10 checkpoint
  // failures land AFTER the delete — so this banner announced IPAM_STILL_BILLING
  // over an IPAM AWS had confirmed deleted, in its loudest position.
  //
  // IpamTeardownPreview.StillBilling is the field that exists so no client
  // derives this, and it is now read. Undefined is the hedge.
  // BillingVerified travels with the StillBilling it qualifies, from this one
  // GET /teardown/preview payload. It never moves the direction — see
  // ipamResidueMeterVerdict — it only stops a verdict Prism cannot confirm from
  // anything current being stated as settled fact.
  const meterVerdict = ipamResidueMeterVerdict({
    stillBilling: previewMeter.stillBilling,
    verified: previewMeter.verified,
    configMessage: teardownProse,
  });

  // WHICH SCREEN A STOPPED TEARDOWN GETS — and why `config.status` alone stopped
  // being the answer.
  //
  // The teardown's settle arm (`run.awaitCascade && ipamDeleted`) writes a
  // combination that did not exist when this route was written:
  //
  //     job status    = blocked          (NON-terminal, so /teardown/active
  //                                       still returns it and the sweep resumes)
  //     config status = partial_failure  (re-beginnable, unlike `blocked`)
  //     blockers      = EMPTY
  //
  // It is the one teardown outcome whose remedy is a single button, and routing
  // on the config status alone sent it to the DASHBOARD, where it surfaced only
  // as a residue banner and a danger-zone entry two clicks away. The panel is the
  // dedicated "your teardown stopped, here is the one thing to do" surface, and
  // it already has an empty-blockers arm built for exactly this state, so the
  // route now reads the JOB as well.
  //
  // It is NOT widened to every non-terminal job: `running` belongs to the
  // in_progress narration above and `awaiting_cascade` never pairs with
  // partial_failure (a run with errors settles the job `failed`). Only `blocked`.
  const teardownJobBlocked = teardownRelevant
    && teardownJob.probed
    && teardownJob.jobStatus === IPAM_TEARDOWN_BLOCKED;

  // THE UNREAD CASE IS HELD, NOT GUESSED. Between the config read landing and the
  // teardown read landing, a partial_failure has no answer to "is a teardown
  // parked on this?" — and rendering the dashboard meanwhile would flash a live
  // capability at an operator one tick before replacing it with a stopped
  // teardown. One request's wait, on the one status that can carry this state.
  const teardownUnresolved = teardownRelevant && !teardownJob.probed;

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{ipamHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading VPC IPAM…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{ipamHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => refresh()} />
        </motion.div>
      </motion.div>
    );
  }

  if (config?.status === 'in_progress') {
    const { title, body } = transitionCopy(config.progressLabel);
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{ipamHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalSectionHeader
              icon={<LanOutlinedIcon sx={{ fontSize: 20 }} />}
              title={title}
              color={IPAM_COLOR}
              mb={1.5}
            />
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              {body}
            </Typography>
            {/* Progress is -1 (indeterminate) for the whole draft phase on purpose:
                a bar that fills to 60% and then stops for a day while a human
                decides is indistinguishable from a hang. */}
            <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // The draft between "begin" and "activate". It is a DURABLE state - a tenant can
  // legitimately sit here for days while a finance approval happens - so this is a
  // resume, not a redirect: the wizard reopens at the cost gate with the draft's
  // own host, Regions and member list read back from the server.
  //
  // The `key` is what makes that resume work. Both this branch and the terminal
  // branch below render <IpamSetup> in the same position, so without distinct keys
  // React would reconcile them as the same instance, `startAt` would be ignored,
  // and an operator who had just pressed "Continue" would be left staring at the
  // Regions step they thought they had finished.
  if (config?.status === 'awaiting_consent') {
    return (
      <IpamSetup
        key="ipam-setup-awaiting-consent"
        startAt={SCOPE_STEP}
        config={config}
        onComplete={refresh}
      />
    );
  }

  // A teardown that could not finish because member accounts still hold live,
  // resource-backed allocations. Also durable, this time waiting on the CUSTOMER's
  // own engineer to delete a VPC - and the IPAM is still there and still billing
  // the whole time, which is why this gets a panel of its own rather than an error
  // card or a route back into the wizard.
  //
  // `job` is deliberately not passed: the panel polls /teardown/active for itself
  // when the prop is absent, and this page does not poll it in the blocked state.
  //
  // IT IS WRAPPED IN THE PAGE HEADER LIKE EVERY OTHER BRANCH. IpamBlockedPanel
  // renders no PortalPageHeader of its own, so returning it bare dropped the
  // "VPC IPAM" title, the icon and the slate accent, and opened the page on a
  // bare red alert with no entrance animation - on the one state where the
  // operator most needs to know which capability is billing them.
  //
  // THE SECOND ROUTE IN IS THE JOB, NOT THE CONFIG. `teardownJobBlocked` is the
  // deleted-and-out-of-budget arm, whose config says `partial_failure`; the panel
  // reads the empty blocker list and the config's own prose and lands on its
  // third arm — "nothing is blocking it", no blocker table, no billing claim, and
  // force delete correctly withheld because the job never asked for one.
  //
  // THE RESIDUE BANNER IS NOT STACKED ON TOP OF IT, and that is the point of
  // returning here rather than falling through. The banner lives on the dashboard
  // branch below and carries its own "Retry teardown" control that navigates to
  // the danger zone, while the panel's button dispatches the retry directly: two
  // controls with one label and two behaviours, over two renderings of the same
  // backend sentence. The panel states the remedy once and owns it.
  if (config?.status === 'blocked' || teardownJobBlocked) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{ipamHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          {/* THE PRE-FLIGHT VERDICT, WHICH THIS PANEL HAS NEVER SEEN. Everything
              it says is about what a teardown DOES; nothing on it says what the
              meter IS, and on this route that is the operator's actual question.
              Passed as the three fields of one payload — see previewMeter. */}
          <IpamBlockedPanel
            config={config}
            onRetried={refresh}
            stillBilling={previewMeter.stillBilling}
            billingVerified={previewMeter.verified}
            billingNote={previewMeter.note}
          />
        </motion.div>
      </motion.div>
    );
  }

  // Held, for one request, while the read above decides between the two screens.
  if (teardownUnresolved) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{ipamHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Checking for a stopped teardown…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // Terminal "nothing is enrolled" states. Re-running the wizard is the ordinary
  // way out of every one of them: BeginIpamConfig UPSERTS the config row rather
  // than deleting and recreating it, so the teardown ledger and the IPAM handle
  // survive, and a fresh begin resets consent and the estimate.
  if (!config || config.status === 'disabled' || config.status === 'failed') {
    return (
      <IpamSetup
        key="ipam-setup-new"
        onComplete={refresh}
        priorFailure={config?.status === 'failed' ? config.errorMessage : null}
      />
    );
  }

  // `config` is passed down rather than left for the dashboard to fetch: it has
  // already been resolved here to decide which of the seven states to render, and
  // IpamDashboard only self-fetches when the prop is absent.
  //
  // THE RESIDUE BANNER IS RENDERED HERE AND NOWHERE ELSE, and only when the
  // best-effort residue map is genuinely non-empty. That condition is what keeps it
  // from doubling the dashboard's own partial_failure alert: a partial_failure with
  // no residue is an ENABLE fan-out that half-succeeded, which the dashboard
  // narrates; a non-empty residue map is a TEARDOWN that half-failed, which is a
  // different fact - named accounts, resources still standing, and a meter that is
  // still running - and the two must not be reported as one.
  //
  // It carries no dismiss control on purpose. A warning that can be closed is not a
  // warning while a meter is running.
  //
  // THE SAME RESIDUE IS PASSED DOWN TO THE DASHBOARD, and that is not a
  // convenience - it is the other half of the contract. IpamDashboard renders its
  // own `partial_failure` alert ("VPC IPAM enrolled some accounts and not
  // others"), which is the ENABLE fan-out's story; it cannot see the residue, so
  // without this prop it narrated a half-failed TEARDOWN as an enrolment problem,
  // stacked directly under this banner saying the opposite. Entry owns the
  // residue banner, the Dashboard owns the enable alert, and `residue` is what
  // lets the Dashboard tell which of the two this partial_failure is.
  return (
    <>
      {residue.length > 0 && (
        <Alert
          severity={meterVerdict.asserted ? 'error' : 'warning'}
          sx={{ mb: 2 }}
          action={(
            <Button
              color="inherit"
              size="small"
              // ?tab=danger, not the bare route: Settings opens on Operating
              // regions, and landing the operator on a Regions form is the wrong
              // destination for the one banner in this capability that fires
              // while a meter is running.
              onClick={() => navigate('/securityhub/ipam/settings?tab=danger')}
            >
              Retry teardown
            </Button>
          )}
        >
          <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
            {meterVerdict.title}
          </AlertTitle>
          Teardown left resources behind in {residue.length} account
          {residue.length === 1 ? '' : 's'}: {residue.map(([accountId]) => accountId).join(', ')}.
          {' '}
          {meterVerdict.line}
          {/* The backend's prose is rendered whenever there is any, not only when
              the banner happens to be hedging: it is the only sentence on this
              card written by something that watched the delete settle. */}
          {teardownProse && (
            <>
              <br /><br />
              <b>{teardownProse}</b>
            </>
          )}
          <br /><br />
          {residue.map(([accountId, detail]) => (
            <span key={accountId} style={{ display: 'block' }}>
              <b>{accountId}</b> — {detail}
            </span>
          ))}
        </Alert>
      )}
      <IpamDashboard config={config} residue={residue} />
    </>
  );
}
